/**
 * Archivo de índice del módulo administrativo
 * Sistema Klever Octopus - Betel Natural
 */

// Componentes principales
export { default as Admin } from './Admin';
export { default as AdminDashboard } from './AdminDashboard';
export { default as Users } from './Users';
export { default as UserForm } from './UserForm';
export { default as UserDetailModal } from './UserDetailModal';
export { default as UserPasswordModal } from './UserPasswordModal';

export { default as Roles } from './Roles';
export { default as RoleForm } from './RoleForm';
export { default as RoleFormPage } from './RoleFormPage';
export { default as RoleDetailModal } from './RoleDetailModal';

export { default as Permissions } from './Permissions';
export { default as PermissionForm } from './PermissionForm';

// Páginas de configuración
export { default as InvoicingSettings } from './Settings/InvoicingSettings';
export { default as PurchaseOrderSettings } from './Settings/PurchaseOrderSettings';
export { default as EmailSettings } from './Settings/EmailSettings';
export { default as SystemSettings } from './Settings/SystemSettings';

/**
 * Configuración de rutas del módulo administrativo
 */
export const adminRoutes = [
  {
    path: '/admin',
    component: 'AdminDashboard',
    permission: 'view_admin_dashboard',
    exact: true,
    title: 'Panel Administrativo'
  },
  {
    path: '/admin/usuarios',
    component: 'Users',
    permission: 'manage_users',
    title: 'Gestión de Usuarios'
  },
  {
    path: '/admin/roles',
    component: 'Roles',
    permission: 'manage_roles',
    title: 'Gestión de Roles'
  },
  {
    path: '/admin/permisos',
    component: 'Permissions',
    permission: 'manage_permissions',
    title: 'Gestión de Permisos'
  }
];

/**
 * Permisos predefinidos del módulo administrativo
 */
export const adminPermissions = [
  // Dashboard administrativo
  {
    name: 'view_admin_dashboard',
    display_name: 'Ver Panel Administrativo',
    description: 'Permite acceder al panel principal de administración',
    module: 'admin',
    group: 'dashboard'
  },
  
  // Gestión de usuarios
  {
    name: 'manage_users',
    display_name: 'Gestionar Usuarios',
    description: 'Acceso completo a la gestión de usuarios',
    module: 'admin',
    group: 'users'
  },
  {
    name: 'create_user',
    display_name: 'Crear Usuarios',
    description: 'Permite crear nuevos usuarios',
    module: 'admin',
    group: 'users'
  },
  {
    name: 'edit_user',
    display_name: 'Editar Usuarios',
    description: 'Permite modificar información de usuarios',
    module: 'admin',
    group: 'users'
  },
  {
    name: 'delete_user',
    display_name: 'Eliminar Usuarios',
    description: 'Permite eliminar usuarios del sistema',
    module: 'admin',
    group: 'users'
  },
  {
    name: 'view_user_details',
    display_name: 'Ver Detalles de Usuarios',
    description: 'Permite ver información detallada de usuarios',
    module: 'admin',
    group: 'users'
  },
  {
    name: 'change_user_password',
    display_name: 'Cambiar Contraseñas',
    description: 'Permite cambiar contraseñas de usuarios',
    module: 'admin',
    group: 'users'
  },
  {
    name: 'toggle_user_status',
    display_name: 'Cambiar Estado de Usuarios',
    description: 'Permite activar/desactivar usuarios',
    module: 'admin',
    group: 'users'
  },
  
  // Gestión de roles
  {
    name: 'manage_roles',
    display_name: 'Gestionar Roles',
    description: 'Acceso completo a la gestión de roles',
    module: 'admin',
    group: 'roles'
  },
  {
    name: 'create_role',
    display_name: 'Crear Roles',
    description: 'Permite crear nuevos roles',
    module: 'admin',
    group: 'roles'
  },
  {
    name: 'edit_role',
    display_name: 'Editar Roles',
    description: 'Permite modificar configuración de roles',
    module: 'admin',
    group: 'roles'
  },
  {
    name: 'delete_role',
    display_name: 'Eliminar Roles',
    description: 'Permite eliminar roles personalizados',
    module: 'admin',
    group: 'roles'
  },
  {
    name: 'assign_role_permissions',
    display_name: 'Asignar Permisos a Roles',
    description: 'Permite configurar permisos de roles',
    module: 'admin',
    group: 'roles'
  },
  
  // Gestión de permisos
  {
    name: 'manage_permissions',
    display_name: 'Gestionar Permisos',
    description: 'Acceso completo a la gestión de permisos',
    module: 'admin',
    group: 'permissions'
  },
  {
    name: 'create_permission',
    display_name: 'Crear Permisos',
    description: 'Permite crear nuevos permisos personalizados',
    module: 'admin',
    group: 'permissions'
  },
  {
    name: 'edit_permission',
    display_name: 'Editar Permisos',
    description: 'Permite modificar permisos existentes',
    module: 'admin',
    group: 'permissions'
  },
  {
    name: 'delete_permission',
    display_name: 'Eliminar Permisos',
    description: 'Permite eliminar permisos personalizados',
    module: 'admin',
    group: 'permissions'
  },
  {
    name: 'sync_system_permissions',
    display_name: 'Sincronizar Permisos del Sistema',
    description: 'Permite sincronizar permisos automáticamente',
    module: 'admin',
    group: 'permissions'
  },
  
  // Configuración del sistema
  {
    name: 'manage_system_config',
    display_name: 'Gestionar Configuración del Sistema',
    description: 'Permite acceder a configuraciones generales',
    module: 'admin',
    group: 'system'
  },
  {
    name: 'view_system_stats',
    display_name: 'Ver Estadísticas del Sistema',
    description: 'Permite ver estadísticas y métricas del sistema',
    module: 'admin',
    group: 'system'
  },
  {
    name: 'export_admin_data',
    display_name: 'Exportar Datos Administrativos',
    description: 'Permite exportar configuraciones y datos',
    module: 'admin',
    group: 'system'
  },
  {
    name: 'import_admin_data',
    display_name: 'Importar Datos Administrativos',
    description: 'Permite importar configuraciones y datos',
    module: 'admin',
    group: 'system'
  }
];

/**
 * Roles predefinidos del sistema administrativo
 */
export const adminRoles = [
  {
    name: 'superadmin',
    display_name: 'Super Administrador',
    description: 'Acceso total al sistema, incluyendo configuración avanzada',
    color: '#dc3545',
    level: 'superadmin',
    is_system: true,
    permissions: adminPermissions.map(p => p.name)
  },
  {
    name: 'admin',
    display_name: 'Administrador',
    description: 'Administrador del sistema con acceso a gestión de usuarios y roles',
    color: '#6f42c1',
    level: 'admin',
    is_system: true,
    permissions: [
      'view_admin_dashboard',
      'manage_users', 'create_user', 'edit_user', 'view_user_details', 'change_user_password', 'toggle_user_status',
      'manage_roles', 'create_role', 'edit_role', 'assign_role_permissions',
      'manage_permissions', 'view_system_stats'
    ]
  },
  {
    name: 'supervisor',
    display_name: 'Supervisor',
    description: 'Supervisor con acceso limitado a funciones administrativas',
    color: '#ffc107',
    level: 'intermediate',
    is_system: true,
    permissions: [
      'view_admin_dashboard',
      'view_user_details', 'toggle_user_status',
      'view_system_stats'
    ]
  }
];

/**
 * Configuración de navegación del módulo administrativo
 */
export const adminNavigation = {
  title: 'Administración',
  icon: 'fas fa-shield-alt',
  items: [
    {
      path: '/admin',
      title: 'Panel Principal',
      icon: 'fas fa-tachometer-alt',
      permission: 'view_admin_dashboard'
    },
    {
      path: '/admin/usuarios',
      title: 'Usuarios',
      icon: 'fas fa-users',
      permission: 'manage_users'
    },
    {
      path: '/admin/roles',
      title: 'Roles',
      icon: 'fas fa-user-shield',
      permission: 'manage_roles'
    },
    {
      path: '/admin/permisos',
      title: 'Permisos',
      icon: 'fas fa-key',
      permission: 'manage_permissions'
    }
  ]
};

/**
 * Configuración del módulo administrativo
 */
export const adminModuleConfig = {
  name: 'admin',
  displayName: 'Administración',
  description: 'Módulo de administración del sistema',
  version: '1.0.0',
  routes: adminRoutes,
  permissions: adminPermissions,
  roles: adminRoles,
  navigation: adminNavigation
};

export default adminModuleConfig;
